import { useCallback, useRef, useState } from "react";
import { TypedDocumentNode, useLazyQuery } from "@apollo/client";
import type { UniversityEmailPreview } from "../queries/emailPreviewQueries";

export function useUniversityEmailPreview<
  TData extends Record<string, UniversityEmailPreview | null | undefined>,
  TVariables extends Record<string, unknown>
>(query: TypedDocumentNode<TData, TVariables>, previewField: keyof TData & string) {
  const [preview, setPreview] = useState<UniversityEmailPreview | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const activeRequestIdRef = useRef(0);

  const [fetchPreview] = useLazyQuery<TData, TVariables>(query, {
    fetchPolicy: "network-only"
  });

  const resetPreview = useCallback(() => {
    activeRequestIdRef.current += 1;
    setPreview(null);
    setError(null);
    setLoading(false);
  }, []);

  const loadPreview = useCallback(
    async (variables: TVariables) => {
      const requestId = activeRequestIdRef.current + 1;
      activeRequestIdRef.current = requestId;

      setLoading(true);
      setPreview(null);
      setError(null);

      const result = await fetchPreview({ variables });

      if (requestId !== activeRequestIdRef.current) {
        return result;
      }

      if (result.error) {
        setError(result.error);
        setPreview(null);
      } else {
        setPreview(result.data?.[previewField] ?? null);
        setError(null);
      }

      setLoading(false);
      return result;
    },
    [fetchPreview, previewField]
  );

  return {
    loadPreview,
    resetPreview,
    preview,
    loading,
    error
  };
}
